import React from 'react';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  visuallyHidden,
} from '../styles';
import { Container, SectionHeader } from './ui';
import logo from '../images/logo.svg';
import three from '../images/three-logo.svg';
import o2 from '../images/o2-logo.svg';
import vodafone from '../images/vodafone-logo.svg';
import tickIcon from '../images/tick-icon.svg';
import crossIcon from '../images/cross-icon.svg';

const StyledCompetitorComparison = styled.section`
  ${sectionMargins()};
`;

const StyledContent = styled.div`
  ${maxBreakpointQuery.mlarge`
    overflow-x: scroll;
  `}
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const StyledTableHeadRow = styled.tr`
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const StyledTableHeadHeader = styled.th`
  width: 20%;
  text-align: center;
  padding: 10px;

  ${minBreakpointQuery.small`
    padding: 20px;
  `}

  ${minBreakpointQuery.medium`
    padding: 30px;
  `}

  &:nth-child(2) {
    background-color: ${brandColours.primary};
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    padding: 20px 10px;

    ${minBreakpointQuery.small`
      padding: 30px 25px;
    `}

    ${minBreakpointQuery.medium`
      padding: 40px 20px;
    `}

    ${minBreakpointQuery.large`
      padding: 50px 25px;
    `}
  }

  span {
    ${visuallyHidden()}
  }
`;

const StyledLogo = styled.img`
  height: 100%;
  width: 100%;
  max-height: 30px;
  max-width: 190px;

  ${minBreakpointQuery.small`
    max-height: 40px;
  `}

  ${minBreakpointQuery.medium`
    max-height: 50px;
  `}

  ${minBreakpointQuery.large`
    max-height: 60px;
  `}
`;

// moved up for targeting
const StyledTableData = styled.td`
  width: 20%;
  text-align: center;
  padding: 25px 5px;

  &:nth-child(2) {
    background-color: ${brandColours.primary};
  }

  ${minBreakpointQuery.small`
    ${fontSize(17)};
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.xxlarge`
    ${fontSize(22)};
  `}
`;

const StyledTableBodyRow = styled.tr`
  display: flex;
  justify-content: space-around;
  align-items: center;
  min-width: 600px;

  &:last-child ${StyledTableData}:nth-child(2) {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }
`;

const StyledTableBodyHeader = styled.th`
  padding: 0 5px;
  width: 20%;
  text-align: center;
  font-weight: ${fontWeights.regular};

  ${minBreakpointQuery.small`
    ${fontSize(17)};
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.xxlarge`
    ${fontSize(22)};
  `}
`;

const StyledIcon = styled.img`
  width: 22px;

  ${minBreakpointQuery.tiny`
    width: 24px;
  `}

  ${minBreakpointQuery.small`
    width: 26px;
  `}

  ${minBreakpointQuery.medium`
    width: 28px;
  `}

  ${minBreakpointQuery.large`
    width: 30px;
  `}
`;

const StyledFooter = styled.footer`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}
`;

const StyledFootnote = styled.p`
  ${fontSize(12)};
  line-height: 1.5;
  white-space: break-spaces;

  ${minBreakpointQuery.small`
    ${fontSize(13)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(14)};
  `}
`;

const CompetitorComparison = ({ heading, text, rows, footnote }) => (
  <StyledCompetitorComparison>
    <Container narrow={true}>
      <SectionHeader heading={heading} text={text} />
      <StyledContent>
        <StyledTable>
          <thead>
            <StyledTableHeadRow>
              <StyledTableHeadHeader scope="col">
                <span>Facts</span>
              </StyledTableHeadHeader>
              <StyledTableHeadHeader scope="col">
                <StyledLogo src={logo} alt="Ecotalk Logo" loading="lazy" />
              </StyledTableHeadHeader>
              <StyledTableHeadHeader scope="col">
                <StyledLogo src={three} alt="Three Logo" loading="lazy" />
              </StyledTableHeadHeader>
              <StyledTableHeadHeader scope="col">
                <StyledLogo src={o2} alt="O2 Logo" loading="lazy" />
              </StyledTableHeadHeader>
              <StyledTableHeadHeader scope="col">
                <StyledLogo src={vodafone} alt="Vodafone Logo" loading="lazy" />
              </StyledTableHeadHeader>
            </StyledTableHeadRow>
          </thead>
          <tbody>
            {rows.map(({ name, items }, id) => (
              <StyledTableBodyRow key={id}>
                <StyledTableBodyHeader scope="row">
                  {name}
                </StyledTableBodyHeader>
                {items.map((item, id) => (
                  <StyledTableData key={id}>
                    {item === true ? (
                      <StyledIcon src={tickIcon} alt="Tick" loading="lazy" />
                    ) : item === false ? (
                      <StyledIcon src={crossIcon} alt="Cross" loading="lazy" />
                    ) : (
                      item
                    )}
                  </StyledTableData>
                ))}
              </StyledTableBodyRow>
            ))}
          </tbody>
        </StyledTable>
      </StyledContent>
      <StyledFooter>
        <StyledFootnote>{footnote}</StyledFootnote>
      </StyledFooter>
    </Container>
  </StyledCompetitorComparison>
);

export default CompetitorComparison;
